import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { UploadPageLayout } from "components/modules/Layout/UploadPageLayout/UploadPageLayout";
import { CropperTutorial } from "components/modules/Tutorial/CropperTutorial/CropperTutorial";
import { CropperContent } from "components/modules/Content/CropperContent/CropperContent";
import Head from "next/head";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { commonLanguageFiles } from "client/config";
import { Translate } from "components/common/Translate/Translate";
import { translateFunc } from "client/utils/translateFunc";
import dynamic from "next/dynamic";
import { CropperJpgContent } from "@/components/modules/Content/CropperContent/CropperJpgContent";
import CropJpgPreToolsArea from "@/components/features/CropJpg/CropJpgPreToolsArea";

const CropUploader = dynamic<{}>(() =>
    import("components/modules/Uploader/Crop/CropUploader").then((mod) => mod.CropUploader),
);

const Page = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
            </Head>
            <UploadPageLayout
                navbar={<Navbar url="/crop-jpg" />}
                title={<Translate keyName="title" />}
                subTitle={<Translate keyName="sub-title" />}
                uploader={<CropUploader />}
                content={<CropperJpgContent />}
                tutorial={<CropperTutorial />}
                preToolsSlot={<CropJpgPreToolsArea />}
                pageUrl="/crop-jpg"
            />
        </>
    );
};

const files = ["crop-jpg-landing", "uploader", ...commonLanguageFiles];

const LanguageWrapper = () => {
    return (
        <LanguageProvider files={files}>
            <Page />
        </LanguageProvider>
    );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
